import React, { useState, useEffect } from "react";
import Navbar from "react-bootstrap/Navbar";
import Container from "react-bootstrap/Container";
import "./navbar.css";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import {
  Avatar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  Badge,
} from "@mui/material";
import { Howl } from "howler"; // Import howler for playing sound
import notificationSoundFile from "../../utilities/notification.mp3";
import { useTheme } from "../../context/ThemeContext";

const MyNavbar = (props) => {

  const [user, setUser] = useState(props.user);
  const [Employee, setEmployee] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [previousNotifications, setPreviousNotifications] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null); // For the notification menu
  const open = Boolean(anchorEl);
  const [newNotificationAlert, setNewNotificationAlert] = useState(false); // To show notification on top of page
  const [previousPendingCount, setPreviousPendingCount] = useState(0); // Track the previous count of pending notifications

  // Private per-user notifications (e.g. "a document was uploaded for you").
  const [userNotifs, setUserNotifs] = useState([]);
  const [empAnchorEl, setEmpAnchorEl] = useState(null);
  const empOpen = Boolean(empAnchorEl);

  // A manager is an Employee-role user flagged as a manager. Managers use the
  // shared admin/manager bell (further down) rather than the regular-employee
  // bell, so their personal notifications are merged INTO that bell.
  const isManager = user.role === "Employee" && Employee?.manager === "yes";
  const unreadUserNotifs = userNotifs.filter((n) => !n.read).length;

  const { theme, toggleTheme } = useTheme();

  // Define the sound using howler
  const notificationSound = new Howl({
    src: [notificationSoundFile], // Add your sound file here
  });

  useEffect(() => {
    const loginEmployee = async () => {
      try {
        const response = await fetch(
          `https://hr-backend-72v1.onrender.com/loginemployee?email=${user.email}`
        );
        const data = await response.json();

        if (response.ok) {
          // console.log('Login successful:', data);
          setEmployee(data);
        } else {
          console.error("Login failed !");
        }
      } catch (error) {
        console.error("Error during login !");
      }
    };

    loginEmployee();
  }, [user.email]);

  // Load this user's own notifications (private) and poll for new ones.
  useEffect(() => {
    if (!user?.email) return;
    const loadUserNotifs = async () => {
      try {
        const res = await fetch(
          `https://hr-backend-72v1.onrender.com/user-notifications?email=${encodeURIComponent(user.email)}`
        );
        if (res.ok) {
          const data = await res.json();
          setUserNotifs(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        /* best-effort */
      }
    };
    loadUserNotifs();
    const id = setInterval(loadUserNotifs, 60000);
    return () => clearInterval(id);
  }, [user?.email]);

  // Fetch the last 5 notifications
  const fetchNotifications = async (isManager = false, employeeEmail = null) => {
    try {
      if (isManager && employeeEmail) {
        // Get local date in ISO format
        const now = new Date();
        const offset = now.getTimezoneOffset();
        const localTime = new Date(now.getTime() - offset * 60000);
        const localDateISO = localTime.toISOString();

        const response = await fetch("https://hr-backend-72v1.onrender.com/check-overwork", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: employeeEmail, date: localDateISO }),
        });
        const data = await response.json();
        return data;
      } else {
        const response = await fetch("https://hr-backend-72v1.onrender.com/recentNotifications");
        const data = await response.json();
        return data;
      }
    } catch (error) {
      // console.error("Error fetching notifications:", error);
      return { notifications: [], pendingCount: 0 }; // Ensure the response format remains consistent
    }
  };

  useEffect(() => {
    // Determine if user should see notifications
    const shouldShowNotifications =
      user.role === "HR" || user.role === "Executive" || Employee?.manager === "yes";

    if (shouldShowNotifications) {
      // Function to fetch and handle notifications
      const handleNotifications = async () => {
        const isManager = Employee?.manager === "yes" && user.role !== "HR" && user.role !== "Executive";
        const response = await fetchNotifications(isManager, Employee?.email);

        let fetchedNotifications = [];
        let pendingCount = 0;

        if (isManager && response.overworked) {
          // For manager, use the overworked array
          fetchedNotifications = response.overworked.map(item => ({
            message: item.message
          }));
          pendingCount = fetchedNotifications.length;
        } else if (response.notifications) {
          // For HR/Executive, use the notifications array as before
          fetchedNotifications = response.notifications;
          pendingCount = response.pendingCount || fetchedNotifications.length;
        }

        if (pendingCount > previousPendingCount) {
          setNotifications(fetchedNotifications); // Update with new notifications
          setNewNotificationAlert(true); // Show new notification alert

          notificationSound.play();

          setPreviousPendingCount(pendingCount);
        } else {
          setNewNotificationAlert(false);

          setPreviousPendingCount(pendingCount);
        }
      };
      handleNotifications();

      // Poll every 30 seconds
      const intervalId = setInterval(handleNotifications, 30000);

      // Cleanup the interval on component unmount
      return () => clearInterval(intervalId);
    }
  }, [user.role, Employee, previousPendingCount]);

  const handleLogout = () => {
    localStorage.removeItem("userValue");
    window.location.reload();
  };

  const handleNotificationClick = (event) => {
    setAnchorEl(event.currentTarget); // Open the notification menu
  };

  const handleClose = () => {
    setAnchorEl(null); // Close the notification menu
  };

  // Mark this user's personal notifications as read (used by both the regular
  // employee bell and the manager's shared bell).
  const markUserNotifsRead = async () => {
    if (!userNotifs.some((n) => !n.read)) return;
    try {
      await fetch("https://hr-backend-72v1.onrender.com/user-notifications/read", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user.email }),
      });
      setUserNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (err) {
      /* best-effort */
    }
  };

  // Identity block shown next to the avatar. Shared by both branches below so the
  // HR/Executive (clickable) and employee variants can never drift apart.
  const profileLines = (
    <>
      <span className="profileName">Logged in as {Employee?.name || user.name}</span>
      <span className="profileRole">{user.role}</span>
    </>
  );

  const openEmpNotifs = (e) => {
    setEmpAnchorEl(e.currentTarget);
    markUserNotifsRead();
  };

  // Managers share the admin/manager bell; opening it also clears their personal
  // (document) notifications.
  const handleAdminBellClick = (event) => {
    handleNotificationClick(event);
    if (isManager) markUserNotifsRead();
  };

  return (
    <div className="header">
      <Navbar className="bg-body-tertiary navbar">
        <Avatar
          sx={{
            bgcolor: "black",
            marginRight: "10px",
            fontSize: "x-large",
            width: "60px",
            height: "60px",
          }}
          src={
            Employee?.profilepic !== "https://via.placeholder.com/250"
              ? Employee?.profilepic
              : null
          }
        >
          {Employee?.profilepic === "https://via.placeholder.com/250" &&
            (Employee?.name || user.name || "")[0]}
        </Avatar>
        {(user.role === "HR" || user.role === "Executive") ? (
          <div
            className="profile"
            onClick={props.onShowProfileOwn}
            style={{ cursor: "pointer" }}
          >
            {profileLines}
          </div>
        ) : (
          <div className="profile">{profileLines}</div>
        )}

        {/* Notification Button for HR or Executive or Manager */}
        {(user.role === "HR" || user.role === "Executive" || Employee?.manager === "yes") && (
          <>
            <IconButton
              onClick={handleAdminBellClick}
              sx={{ marginRight: "20px" }}
            >
              <Badge
                badgeContent={isManager ? unreadUserNotifs : 0}
                color="warning"
              >
                <NotificationsNoneIcon
                  fontSize="medium"
                  sx={{ color: "white" }}
                />
              </Badge>
            </IconButton>

            {/* Notification Menu */}
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
            >
              <p style={{ textAlign: "center", color: "gray" }}>
                {Employee?.manager === "yes" && user.role !== "HR" && user.role !== "Executive"
                  ? "Recent Notifications"
                  : "Recent Notifications ... Go to Requests Tab to view all Notifications"}
              </p>
              {notifications.length === 0 && !(isManager && userNotifs.length > 0) ? (
                <MenuItem onClick={handleClose}>
                  <Alert severity="info">No new notifications</Alert>
                </MenuItem>
              ) : (
                <>
                  {notifications.map((notification, index) => (
                    <MenuItem key={index}>
                      <Alert
                        sx={{ width: "100%" }}
                        severity="info"
                      >
                        {notification.message
                          ? notification.message
                          : `${notification.name} requested that their '${notification.field}' should be changed to '${notification.value}'`}
                      </Alert>
                    </MenuItem>
                  ))}
                  {/* Document / personal notifications surfaced in the manager's bell */}
                  {isManager &&
                    userNotifs.map((n, i) => (
                      <MenuItem key={`un-${i}`}>
                        <Alert sx={{ width: "100%" }} severity="info">
                          {n.message}
                        </Alert>
                      </MenuItem>
                    ))}
                </>
              )}
            </Menu>
          </>
        )}

        {/* Private notifications for a regular employee (shown only when they have any) */}
        {user.role === "Employee" && Employee?.manager !== "yes" && userNotifs.length > 0 && (
          <>
            <IconButton onClick={openEmpNotifs} sx={{ marginRight: "20px" }}>
              <Badge
                badgeContent={userNotifs.filter((n) => !n.read).length}
                color="warning"
              >
                <NotificationsNoneIcon fontSize="medium" sx={{ color: "white" }} />
              </Badge>
            </IconButton>
            <Menu
              anchorEl={empAnchorEl}
              open={empOpen}
              onClose={() => setEmpAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <p style={{ textAlign: "center", color: "gray" }}>Notifications</p>
              {userNotifs.map((n, index) => (
                <MenuItem key={index}>
                  <Alert sx={{ width: "100%" }} severity="info">
                    {n.message}
                  </Alert>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}

        {/* Theme Toggle Button */}
        <IconButton
          onClick={toggleTheme}
          className="themeToggleBtn"
          title={theme === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"}
          sx={{ marginRight: "10px" }}
        >
          {theme === "light" ? (
            <DarkModeIcon sx={{ color: "white" }} />
          ) : (
            <LightModeIcon sx={{ color: "white" }} />
          )}
        </IconButton>

        <button className="logoutBtn">
          <LogoutIcon onClick={handleLogout} />
        </button>
      </Navbar>

      {/* Top Page Notification Alert */}
      {newNotificationAlert && (
        <div className="top-notification">
          <Alert severity="info" onClose={() => setNewNotificationAlert(false)}>
            You have new notifications!
          </Alert>
        </div>
      )}
    </div>
  );
};

export default MyNavbar;
